// Body of one footer column (footerNav.js): its sub-groups, each a menu whose
// title is a button (aria-expanded) toggling its children. Every menu starts
// closed -- on every screen size and for every role -- and only opens on a
// click; a role change (Switch Role, sign in/out: `roleKey`) closes them all.
// `Item` is the footer's own MenuItem, so links keep the footer's exact look
// and animation settings; inert (disabled in Animation Settings) entries never
// navigate. "auth" / "roleSwitch" bodies are rendered by the caller
// (renderAuth / renderRoleSwitch).
import React, { useState } from "react";
import { FaChevronDown } from "react-icons/fa";

const FooterNavGroups = ({ columnKey, roleKey = "", groups = [], Item, itemProps = {}, onAction, renderAuth, renderRoleSwitch }) => {
  // { roleKey, menus: { [groupKey]: true } }. A new roleKey resets it right away (during
  // render, so no frame shows the old menus), and coming back to a previous role never
  // restores the menus that were open under it.
  const [openMenus, setOpenMenus] = useState({ roleKey, menus: {} });
  if (openMenus.roleKey !== roleKey) setOpenMenus({ roleKey, menus: {} });
  const menus = openMenus.roleKey === roleKey ? openMenus.menus : {};

  if (!groups.length) return null;

  const isOpen = (group) => menus[group.key] === true;
  const toggleMenu = (group) => setOpenMenus({ roleKey, menus: { ...menus, [group.key]: !isOpen(group) } });

  const renderItem = (item, index) => {
    const delay = itemProps.enableItemAnimations ? 100 + index * 50 : 0;
    if (item.status !== "active") {
      return (
        <h6 key={item.key} className="mt-0 mb-0 pt-1 pb-1 footer-item footer-mb-inert no-item-animation">
          <span className="footer-mb-inert-link" aria-disabled="true">
            {item.icon && <item.icon className="mr-2" aria-hidden="true" />}
            <span className="footer-link-text">{item.label}</span>
            <span className="footer-mb-badge">{item.statusText}</span>
          </span>
        </h6>
      );
    }
    if (item.action) {
      return (
        <Item key={item.key} to={item.href} icon={item.icon} onClick={() => onAction?.(item.action)} delay={delay} {...itemProps}>
          {item.label}
        </Item>
      );
    }
    return (
      <Item key={item.key} to={item.route} icon={item.icon} delay={delay} {...itemProps}>
        {item.label}
      </Item>
    );
  };

  const renderBody = (group) => {
    if (group.kind === "auth") return renderAuth?.();
    if (group.kind === "roleSwitch") return renderRoleSwitch?.();
    return group.items.map(renderItem);
  };

  return (
    <div className="footer-nav-groups" data-testid={`footer-column-${columnKey}`}>
      {groups.map((group) => {
        const open = isOpen(group);
        const panelId = `footer-${columnKey}-${group.key}`;
        return (
          <div key={group.key} className={`footer-mb-group footer-group-${group.kind}${open ? " is-open" : ""}`} data-group={group.key}>
            <button
              type="button"
              className="footer-mb-toggle"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => toggleMenu(group)}
            >
              <span className="footer-mb-toggle-text">{group.title}</span>
              <FaChevronDown className="footer-mb-chevron" aria-hidden="true" />
            </button>
            <div id={panelId} className="footer-mb-panel" hidden={!open}>
              {renderBody(group)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FooterNavGroups;
